function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { lazy, Suspense, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import useStyles from 'isomorphic-style-loader/useStyles';
import cn from 'classnames';
import get from 'lodash/get';
import includes from 'lodash/includes';
import MetricConversions from 'libs/MetricConversions';
import Select from 'components/Form/Select';
import messages from 'components/Charts/messages';
import MealTooltip from 'components/RelatedData/MealTooltip';
import ActivityIcon from 'svg/activity.svg';
import InjectionIcon from 'svg/injection.svg';
import MealIcon from 'svg/meal.svg';
import App from 'modules/App';
import Account from 'modules/Account';
import { RELATED_DATA_CHART_OPTIONS } from '../../constants';
import { useAdditionalData } from './hooks';
import Meal from './Meal';
import Tooltip from './Tooltip';
import styles from './AdditionalCharts.pcss';
const ResponsiveLine = /*#__PURE__*/ lazy(()=>import('@nivo/line').then((m)=>({
            default: m.ResponsiveLine
        })));
const colors = {
    medications: '#19D8E6',
    foods: '#A6E06C',
    activities: '#9982ED',
    weight: '#f7cc6e',
    height: '#86c9f9',
    note: '#c7dae1'
};
const AdditionalCharts = ({ firstTick, lastTick, relatedDataTimeRef, isInProgress = false, isTooltipDisabled = false, onMouseMoveChart, onMouseLeaveChart })=>{
    useStyles(styles);
    const intl = useIntl();
    const direction = useSelector(App.selectors.direction);
    const metricsUnits = useSelector(Account.selectors.metricsUnits);
    const metricConversions = new MetricConversions(metricsUnits);
    const [highlightedItem, setHighlightedItem] = useState(null);
    const [highlightedItemType, setHighlightedItemType] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const { relatedDataGrouped, measurementsGrouped, meals } = useAdditionalData(firstTick, lastTick);
    const onMouseHoverPoint = (point)=>{
        const serieId = point.serieId;
        const startTime = point.data.xStart || point.data.x;
        const parsedSerieId = serieId.replace('-end', '');
        if (startTime !== highlightedItem || parsedSerieId !== highlightedItemType) {
            setHighlightedItem(startTime);
            setHighlightedItemType(parsedSerieId);
        }
    };
    const onMouseLeave = (evt)=>{
        if (onMouseLeaveChart) {
            onMouseLeaveChart(evt);
        }
        setHighlightedItem(null);
        setHighlightedItemType(null);
    };
    const onSelectOptionMenu = (selectedOption)=>{
        const { value } = selectedOption;
        const newSelectedOptions = [
            ...selectedOptions
        ];
        const indexOption = newSelectedOptions.indexOf(value);
        if (indexOption >= 0) {
            newSelectedOptions.splice(indexOption, 1);
        } else {
            newSelectedOptions.push(value);
        }
        setSelectedOptions(newSelectedOptions);
    };
    const renderMealTooltip = (pointTooltipProps)=>{
        const meal = get(pointTooltipProps, 'point.data.meal');
        if (isTooltipDisabled || isInProgress || !meal) {
            return null;
        }
        return /*#__PURE__*/ _jsx(MealTooltip, {
            meal: meal
        });
    };
    const renderMeals = ()=>{
        if (!selectedOptions.includes('carbs')) {
            return null;
        }
        const margin = {
            top: 0,
            right: 50,
            bottom: 0,
            left: 50
        };
        const onMouseMove = (evt)=>{
            if (onMouseMoveChart) onMouseMoveChart(evt, margin, true);
        };
        const renderCarb = (pointSymbolProps)=>{
            const { meal } = pointSymbolProps.datum;
            if (!meal) {
                return null;
            }
            // const isHighlighted = highlightedItem && x
            //   && highlightedItem.toString() === x.toString()
            //   && highlightedItemType === 'carb';
            // const highlightedStyle = isHighlighted ? { border: '1px solid black', strokeWidth: 1, stroke: 'black' } : {};
            return /*#__PURE__*/ _jsx(Meal, {
                carbs: meal.carbs,
                imageHref: meal.image
            });
        };
        const title = /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.carbs));
        const icon = /*#__PURE__*/ _jsx(MealIcon, {});
        const unit = /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.nutrientUnit));
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("p", {
                    className: styles.relatedLabel,
                    children: [
                        title,
                        " (",
                        unit,
                        ")"
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: `nivoChart ${styles.root_related} ${styles.root_relatedMeals}`,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: cn('nivoChart__inner', styles.root_related__inner, {
                            fadingLoader: isInProgress
                        }),
                        onMouseMove: onMouseMove,
                        onMouseLeave: onMouseLeave,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `${styles.related_iconWrapper} ${styles.related_iconWrapperFoods}`,
                                children: icon
                            }),
                            /*#__PURE__*/ _jsx(Suspense, {
                                fallback: null,
                                children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                                    data: meals,
                                    margin: margin,
                                    layers: [
                                        'points',
                                        'mesh'
                                    ],
                                    pointSymbol: renderCarb,
                                    xScale: {
                                        type: 'linear',
                                        min: firstTick,
                                        max: lastTick,
                                        reverse: direction === 'rtl'
                                    },
                                    yScale: {
                                        type: 'linear',
                                        min: 0,
                                        max: 1
                                    },
                                    tooltip: renderMealTooltip,
                                    useMesh: true,
                                    onMouseMove: onMouseHoverPoint
                                })
                            })
                        ]
                    })
                })
            ]
        });
    };
    const renderTooltip = (pointTooltipProps)=>{
        if (isTooltipDisabled || isInProgress) {
            return null;
        }
        return /*#__PURE__*/ _jsx(Tooltip, _object_spread({}, pointTooltipProps));
    };
    const renderPoint = (point, type, layer)=>{
        const { xScale } = layer;
        const { x, y, xEnd, xStart } = point.data;
        if (xStart) {
            return null;
        }
        const parsedType = type.replace('-end', '');
        const isHighlighted = highlightedItem && x ? highlightedItem.toString() === x.toString() && parsedType === highlightedItemType : false;
        const highlightedStyle = isHighlighted ? {
            border: '1px solid black',
            strokeWidth: 1,
            stroke: 'black'
        } : {};
        const width = Math.max(xScale(xEnd) - xScale(x), 1);
        const pos = xScale(xStart || x);
        return /*#__PURE__*/ _jsx("g", {
            transform: `translate(${pos},15)`,
            children: /*#__PURE__*/ _jsx("rect", {
                rx: "4",
                y: "0",
                opacity: 0.75,
                width: width,
                height: 16,
                fill: colors[type],
                style: _object_spread({
                    pointerEvents: 'none'
                }, highlightedStyle)
            })
        }, `${type}-${x}-${y}`);
    };
    const renderMeasurementPoint = (point, type, layer)=>{
        const { xScale, yScale } = layer;
        const { x, y, yEnd, xEnd, prevY = 0, xStart } = point.data;
        if (xStart) {
            return null;
        }
        const width = Math.max(xScale(xEnd || x) - xScale(x), 0) + 1;
        const height = y > 0 ? yScale(y) : 0;
        const nextDataIsBigger = yEnd && y > yEnd;
        const rightRadius = nextDataIsBigger ? Math.min(4, yScale(y) - yScale(yEnd), xScale(xEnd || x) - xScale(x)) : 0;
        const prevDataIsBigger = prevY && prevY > y;
        const leftRadius = prevDataIsBigger ? 0 : Math.min(4, yScale(y) - yScale(prevY), xScale(xEnd || x) - xScale(x));
        const parsedType = type.replace('-end', '');
        const xTimestamp = xStart || x;
        const pos = xScale(xTimestamp);
        const isHighlighted = highlightedItem && xTimestamp && height > 0 ? highlightedItem.toString() === xTimestamp.toString() && parsedType === highlightedItemType : false;
        return /*#__PURE__*/ _jsx("g", {
            transform: `translate(${pos},0)`,
            children: /*#__PURE__*/ _jsx("path", {
                stroke: "black",
                strokeWidth: isHighlighted ? 1 : 0,
                d: `M 0 ${height} V
            ${leftRadius} C 0 0 ${leftRadius} 0 ${leftRadius} 0 Q ${rightRadius} 0
            ${width - rightRadius - leftRadius} 0 Q ${width} 0 ${width} ${rightRadius} V ${height} z`,
                opacity: 0.75,
                fill: colors[type]
            })
        }, `${type}-${x}-${y}`);
    };
    const renderMeasurementPoints = (layer, type)=>{
        const { points } = layer;
        return points.map((point)=>renderMeasurementPoint(point, type, layer));
    };
    const renderMeasurements = (type)=>{
        if (!selectedOptions.includes(type)) {
            return null;
        }
        const margin = {
            top: 15,
            right: 50,
            bottom: 30,
            left: 50
        };
        const tickValues = [
            0
        ];
        if (measurementsGrouped[type].maxValue) {
            tickValues.push(measurementsGrouped[type].maxValue);
        }
        const axisY = {
            tickSize: 0,
            tickPadding: 18,
            tickRotation: 0,
            tickValues
        };
        const theme = {
            text: {
                fontSize: 12,
                fill: '#6F7EA2'
            }
        };
        const onMouseMove = (evt)=>{
            if (onMouseMoveChart) onMouseMoveChart(evt, margin, true);
        };
        const measurementsLayer = (layer)=>renderMeasurementPoints(layer, type);
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("p", {
                    className: styles.relatedLabel,
                    children: [
                        /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels[type])),
                        " (",
                        metricConversions[type].unitSymbol,
                        ")"
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: `nivoChart ${styles.root_measurements}`,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: cn('nivoChart__inner', styles.root_related__inner, {
                            fadingLoader: isInProgress
                        }),
                        onMouseMove: onMouseMove,
                        onMouseLeave: onMouseLeave,
                        children: /*#__PURE__*/ _jsx(Suspense, {
                            fallback: null,
                            children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                                data: [
                                    measurementsGrouped[type]
                                ],
                                margin: margin,
                                theme: theme,
                                layers: [
                                    'axes',
                                    measurementsLayer,
                                    'mesh'
                                ],
                                axisRight: direction === 'rtl' ? axisY : null,
                                axisLeft: direction === 'rtl' ? null : axisY,
                                axisTop: null,
                                axisBottom: null,
                                xScale: {
                                    type: 'linear',
                                    min: firstTick,
                                    max: lastTick,
                                    reverse: direction === 'rtl'
                                },
                                yScale: {
                                    type: 'linear',
                                    min: 0,
                                    max: measurementsGrouped[type].maxValue
                                },
                                tooltip: renderTooltip,
                                useMesh: true,
                                onMouseMove: onMouseHoverPoint
                            })
                        })
                    })
                })
            ]
        });
    };
    const renderRanges = (layer, type)=>{
        const { points } = layer;
        return points.map((point)=>renderPoint(point, type, layer));
    };
    const renderRelated = (type)=>{
        if (!selectedOptions.includes(type)) {
            return null;
        }
        const relatedLength = relatedDataGrouped[type][0].data.length;
        const margin = {
            top: 0,
            right: 50,
            bottom: 30,
            left: 50
        };
        const title = /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels[type]));
        let icon;
        let unit;
        switch(type){
            case 'activities':
                icon = /*#__PURE__*/ _jsx(ActivityIcon, {});
                unit = /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.activityUnit));
                break;
            case 'medications':
                icon = /*#__PURE__*/ _jsx(InjectionIcon, {});
                unit = /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.injectionUnit));
                break;
            default:
                break;
        }
        const onMouseMove = (evt)=>{
            if (onMouseMoveChart) onMouseMoveChart(evt, margin, true);
        };
        const relatedDataLayer = (layer)=>renderRanges(layer, type);
        /**
     * Mesh crashes on hover if empty chart, so we turn it on only if relatedLength is greater than 0
     * https://github.com/plouc/nivo/issues/2597
     */ return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("p", {
                    className: styles.relatedLabel,
                    children: [
                        title,
                        " (",
                        unit,
                        ")"
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: `nivoChart ${styles.root_related}`,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: cn('nivoChart__inner', styles.root_related__inner, {
                            fadingLoader: isInProgress
                        }),
                        onMouseMove: onMouseMove,
                        onMouseLeave: onMouseLeave,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.related_iconWrapper,
                                children: icon
                            }),
                            /*#__PURE__*/ _jsx(Suspense, {
                                fallback: null,
                                children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                                    data: relatedDataGrouped[type],
                                    margin: margin,
                                    layers: [
                                        relatedDataLayer,
                                        relatedLength ? 'mesh' : null
                                    ],
                                    xScale: {
                                        type: 'linear',
                                        min: firstTick,
                                        max: lastTick,
                                        reverse: direction === 'rtl'
                                    },
                                    yScale: {
                                        type: 'linear',
                                        min: 0,
                                        max: 10
                                    },
                                    tooltip: renderTooltip,
                                    useMesh: true,
                                    onMouseMove: onMouseHoverPoint
                                })
                            })
                        ]
                    })
                })
            ]
        });
    };
    let className = '';
    if (selectedOptions.length > 0) {
        className += `${styles.timeSelector__chart} ${styles.timeSelector_relatedData__chart} `;
    }
    if (includes(selectedOptions, 'carbs')) {
        className += `${styles.timeSelector_relatedData__chartMeals} `;
        if (selectedOptions.length === 1) {
            className += `${styles.timeSelector_relatedData__chartMealsOnly}`;
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles.related__selectContainer,
                children: /*#__PURE__*/ _jsx(Select, {
                    id: "relatedData",
                    optionsFrom: RELATED_DATA_CHART_OPTIONS.map((option)=>({
                            option,
                            label: intl.formatMessage(messages.labels[option])
                        })),
                    noValueMessage: messages.placeholders.additionalChart,
                    onChange: onSelectOptionMenu,
                    valueKey: "option",
                    labelKey: "label",
                    value: selectedOptions,
                    className: styles.related__selectWrapper,
                    multi: true
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.related_wrapper,
                children: [
                    renderMeals(),
                    renderRelated('medications'),
                    renderRelated('activities'),
                    renderMeasurements('weight'),
                    renderMeasurements('height'),
                    /*#__PURE__*/ _jsx("div", {
                        ref: relatedDataTimeRef,
                        className: className
                    })
                ]
            })
        ]
    });
};
export default AdditionalCharts;
